/**
 * SURGICAL cell patching for SDMS workbooks — replaces ONLY the changed `<c>`
 * elements inside `xl/worksheets/sheetN.xml` and rewrites just that zip entry.
 *
 * WHY THIS EXISTS (client 2026-08-19, "the sheet is blank in ALPACA after I edit
 * it in Intelligence"). The save used to round-trip the whole workbook through
 * ExcelJS — `wb.xlsx.load(buf)` … `wb.xlsx.writeBuffer()`. DevExpress REFUSES to
 * open the worksheet XML ExcelJS emits: `Workbook.LoadDocument` returns **false**
 * and the control falls back to a blank default document, which is why LIMS
 * showed a single empty `Sheet1` while our own reader was perfectly happy.
 *
 * Proven with the real DevExpress v20.1 engine, swapping one ExcelJS-written part
 * at a time into a known-good workbook:
 *
 *     original                              -> loaded=True   sheets=3
 *     original + ExcelJS xl/workbook.xml    -> loaded=True   sheets=3
 *     original + ExcelJS xl/styles.xml      -> loaded=True   sheets=3
 *     original + ExcelJS worksheets/sheet1  -> loaded=FALSE  sheets=1   <-- the culprit
 *
 * A plain load→write with ZERO edits reproduces it, so the trigger is ExcelJS's
 * serializer, not our edits. Two plausible-looking causes were tested and
 * disproved on the way: the bare zip directory entries ExcelJS adds, and the
 * dropped `localSheetId` on `_xlnm._FilterDatabase` — restoring each individually
 * still failed to load.
 *
 * So: never re-serialize. Every other byte of the package stays exactly as
 * DevExpress wrote it. Re-zipping itself is safe — the hybrids above were rebuilt
 * zips and loaded fine.
 */
import JSZip from 'jszip';

/** One edited cell. `row`/`col` are 0-based, matching Univer's model. */
export interface SheetCellEdit {
  row: number;
  col: number;
  value: unknown;
  /** Write the value as TEXT even when it looks like a number — a FIELD value (DevExpress FIELD() returns text). */
  text?: boolean;
}

/** One cell whose FORMULA is (re)written — Apply-Rounding `_LIMSRound(...)` wraps
 *  (OG RoundCellInPlace parity). `row`/`col` are 0-based; `formula` is the inner
 *  expression WITHOUT the leading `=` (OOXML `<f>` never carries one); `value` is
 *  the cached computed result written to `<v>`. */
export interface SheetFormulaEdit {
  row: number;
  col: number;
  formula: string;
  value: unknown;
  /** The number format the cell now shows — OG RoundCellInPlace sets it from the ROUNDED value (8.3 → "0.0").
   *  Written as a copy of the cell's style with this format (addNumFmtStyle); omitted = the style is kept. */
  numFmt?: string;
}

/** 0-based (row, col) → an A1 reference. */
export function a1(row0: number, col0: number): string {
  let n = col0 + 1;
  let s = '';
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = (n - 1 - m) / 26;
  }
  return `${s}${row0 + 1}`;
}

/** A1 column letters → 0-based index. */
function colOf(ref: string): number {
  const m = /^([A-Z]+)/.exec(ref);
  if (!m) return -1;
  let n = 0;
  for (const ch of m[1]!) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Build the replacement `<c>`. The existing STYLE attribute is carried over so
 * the cell keeps its formatting; the type is re-derived from the new value.
 * A numeric value is written bare (`<v>`), text as an inline string — never as a
 * shared string, because appending to `sharedStrings.xml` would mean rewriting a
 * second part and renumbering every index in the sheet.
 */
function buildCell(ref: string, styleAttr: string, value: unknown, text = false): string {
  const raw = value == null ? '' : String(value);
  const trimmed = raw.trim();
  if (!text && trimmed !== '' && /^-?\d+(\.\d+)?$/.test(trimmed))
    return `<c r="${ref}"${styleAttr}><v>${trimmed}</v></c>`;
  if (trimmed === '') return `<c r="${ref}"${styleAttr}/>`;
  return `<c r="${ref}"${styleAttr} t="inlineStr"><is><t xml:space="preserve">${esc(raw)}</t></is></c>`;
}

/** Apply one cell edit to a worksheet XML string. Returns the new XML. */
export function patchCell(xml: string, ref: string, value: unknown, text = false): string {
  // 1. The cell already exists — replace it in place, keeping its style.
  const cellRe = new RegExp(`<c r="${ref}"([^>]*?)(?:/>|>[\\s\\S]*?</c>)`);
  const hit = cellRe.exec(xml);
  if (hit) {
    const styleMatch = / s="\d+"/.exec(hit[1] ?? '');
    return xml.slice(0, hit.index) + buildCell(ref, styleMatch?.[0] ?? '', value, text)
      + xml.slice(hit.index + hit[0].length);
  }

  // 2. The cell is absent but its ROW exists — insert in column order.
  const rowNum = ref.replace(/^[A-Z]+/, '');
  const col = colOf(ref);
  const rowRe = new RegExp(`<row r="${rowNum}"([^>]*?)(?:/>|>([\\s\\S]*?)</row>)`);
  const rowHit = rowRe.exec(xml);
  if (rowHit) {
    const inner = rowHit[2] ?? '';
    const cells = [...inner.matchAll(/<c r="([A-Z]+\d+)"/g)];
    const before = cells.find(c => colOf(c[1]!) > col);
    const newCell = buildCell(ref, '', value, text);
    const newInner = before
      ? inner.slice(0, before.index) + newCell + inner.slice(before.index)
      : inner + newCell;
    const rebuilt = `<row r="${rowNum}"${rowHit[1] ?? ''}>${newInner}</row>`;
    return xml.slice(0, rowHit.index) + rebuilt + xml.slice(rowHit.index + rowHit[0].length);
  }

  // 3. Neither exists — append a row inside <sheetData>, in row order.
  const newRow = `<row r="${rowNum}">${buildCell(ref, '', value, text)}</row>`;
  const rows = [...xml.matchAll(/<row r="(\d+)"/g)];
  const after = rows.find(r => parseInt(r[1]!, 10) > parseInt(rowNum, 10));
  if (after) return xml.slice(0, after.index) + newRow + xml.slice(after.index);
  const close = xml.lastIndexOf('</sheetData>');
  if (close < 0) return xml;                       // not a worksheet part — leave untouched
  return xml.slice(0, close) + newRow + xml.slice(close);
}

/**
 * Refresh a FORMULA cell's cached value, keeping its `<f>` untouched.
 *
 * DevExpress renders the CACHED value and does not recalculate on load — proven:
 * `<calcPr fullCalcOnLoad="1"/>` makes no difference to it. So after an edit the
 * dependent formulas kept their old numbers in LIMS (edit F11 to 1.23 and G11
 * still read 104.6 for `=F11*20`) while Intelligence showed the recomputed value,
 * because Univer calculates live. Writing the recomputed value into `<v>` makes
 * the stored file self-consistent for any reader.
 *
 * A string result carries `t="str"`, which is what DevExpress itself writes for
 * one; a numeric result carries no type attribute.
 */
export function patchFormulaCache(xml: string, ref: string, value: unknown): string {
  const re = new RegExp(`<c r="${ref}"([^>]*?)>([\\s\\S]*?)</c>`);
  const hit = re.exec(xml);
  if (!hit) return xml;
  const inner = hit[2] ?? '';
  const fMatch = /<f[^>]*>[\s\S]*?<\/f>|<f[^>]*\/>/.exec(inner);
  if (!fMatch) return xml;                       // not a formula cell — never touch it
  const style = / s="\d+"/.exec(hit[1] ?? '')?.[0] ?? '';
  const raw = value == null ? '' : String(value);
  const trimmed = raw.trim();
  // The result's TYPE decides, not its look: a formula that returns TEXT — TEXT(G8,0), a spliced "1.108974717"
  // literal — is a string result even when it reads as a number, and DevExpress stores it t="str" (PFAS 533 J8,
  // OG QB26093004_001). Saved as a number it took the cell's 0.00 format, so the save stored 171.48 where OG
  // stores 171.480527 (side-by-side PFAS batches, 2026-09-30). Univer hands numbers over as numbers.
  const isNum = typeof value === 'number' ? Number.isFinite(value)
    : typeof value === 'string' ? false
      : trimmed !== '' && /^-?\d+(\.\d+)?$/.test(trimmed);
  const typeAttr = isNum ? '' : ' t="str"';
  const cached = isNum ? `<v>${trimmed}</v>` : trimmed === '' ? '<v/>' : `<v>${esc(raw)}</v>`;
  return xml.slice(0, hit.index)
    + `<c r="${ref}"${style}${typeAttr}>${fMatch[0]}${cached}</c>`
    + xml.slice(hit.index + hit[0].length);
}

/** XML-escape formula text for `<f>`. Excel escapes only `&`, `<`, `>` inside a
 *  formula element and leaves string-literal double quotes (e.g. `"3"`) as-is, so
 *  this deliberately does NOT touch `"` — unlike `esc()`, which quotes them. */
function escFormula(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * (Re)write a cell's FORMULA in place — used to persist Apply-Rounding's
 * `=_LIMSRound(...)` wraps into `<f>` so the saved blob carries the wrap, exactly
 * as OG `RoundCellInPlace` does (the wrap IS the rounding marker that round-trips
 * through Save/Retrieve). The cell's STYLE is preserved (display precision comes
 * from the live NumberFormat, which is a nicety — the wrap is the essential part).
 *
 * `formula` is the inner expression WITHOUT the leading `=`. The cached `<v>` is typed by the VALUE, as
 * patchFormulaCache does: a number is a numeric cache; a TEXT result is cached as text (`t="str"`), even when
 * it reads as a number — DevExpress stores a text result that way. This writer typed by the LOOK: the import's
 * spliced PFAS Numeric Result `IF("-1"="1", TEXT(G59,0), "0.088222304")` was cached as the number 0.088222304
 * and "Not detected" was not cached at all, where OG stores both as text (side-by-side PFAS QB26100102_001 /
 * QB26100101_001, 2026-10-01). An empty or missing value still writes no cache (the reader recomputes). If the
 * cell is absent it is left untouched (a rounded Result cell always exists — it carried a template formula).
 */
export function patchCellFormula(xml: string, ref: string, formula: string, value: unknown): string {
  const re = new RegExp(`<c r="${ref}"([^>]*?)(?:/>|>[\\s\\S]*?</c>)`);
  const hit = re.exec(xml);
  if (!hit) return xml;                               // cell not present — never create one here
  const style = / s="\d+"/.exec(hit[1] ?? '')?.[0] ?? '';
  const raw = value == null ? '' : String(value).trim();
  const isNum = typeof value === 'number' ? Number.isFinite(value)
    : typeof value === 'string' ? false
      : raw !== '' && /^-?\d+(\.\d+)?$/.test(raw);
  const isText = !isNum && typeof value === 'string' && raw !== '';
  const typeAttr = isText ? ' t="str"' : '';
  const cached = isNum ? `<v>${raw}</v>` : isText ? `<v>${esc(String(value))}</v>` : '';   // empty → reader recomputes
  return xml.slice(0, hit.index)
    + `<c r="${ref}"${style}${typeAttr}><f>${escFormula(formula)}</f>${cached}</c>`
    + xml.slice(hit.index + hit[0].length);
}

/** Built-in SpreadsheetML number formats — a code listed here needs no <numFmt> entry. */
const BUILTIN_NUM_FMT_IDS: Record<string, number> = { General: 0, '0': 1, '0.00': 2, '#,##0': 3, '#,##0.00': 4, '0%': 9, '0.00%': 10 };
const escAttr = (v: string) => v.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const unescAttr = (v: string) => v.replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');

/** A number format ExcelJS wrote from an OBJECT (`{id, formatCode}`) instead of its code string. DevExpress
 *  rejects the whole workbook for it — LoadDocument returns false and ALPACA opens the batch as an empty
 *  Sheet1 (QB26101301_001, 2026-10-13: a conditional-format style on template 8114). */
const OBJECT_NUM_FMT = 'formatCode="[object Object]"';

/** `stylesXml` with every `[object Object]` format code replaced by General (the original code is gone, so
 *  General is the only safe value; the rule's colours are untouched) — the same string when there is none. */
export function repairObjectNumFmts(stylesXml: string): string {
  return stylesXml.includes(OBJECT_NUM_FMT) ? stylesXml.split(OBJECT_NUM_FMT).join('formatCode="General"') : stylesXml;
}

/** `stylesXml` with a cell style that copies style `baseXf` but shows number format `code`, and that style's
 *  index. The format is added to <numFmts> unless it is built in or already there; when the base style already
 *  shows it, the base index is returned unchanged. Null when styles.xml has no <cellXfs>. */
export function addNumFmtStyle(stylesXml: string, baseXf: number, code: string): { xml: string; index: number } | null {
  if (typeof code !== 'string' || !code) return null;   // never write a non-string code (see OBJECT_NUM_FMT)
  let xml = stylesXml;
  let id = BUILTIN_NUM_FMT_IDS[code];
  if (id === undefined) {
    const fmts = [...xml.matchAll(/<numFmt\b[^>]*\/>/g)].map(m => m[0]);
    const idOf = (t: string) => Number(/numFmtId="(\d+)"/.exec(t)?.[1] ?? NaN);
    const found = fmts.find(t => unescAttr(/formatCode="([^"]*)"/.exec(t)?.[1] ?? '') === code);
    if (found) id = idOf(found);
    else {
      id = Math.max(163, ...fmts.map(idOf).filter(Number.isFinite)) + 1;
      const el = `<numFmt numFmtId="${id}" formatCode="${escAttr(code)}"/>`;
      xml = /<numFmts\b/.test(xml)
        ? xml.replace(/<numFmts\b([^>]*)>([\s\S]*?)<\/numFmts>/, (_m, a: string, inner: string) =>
            `<numFmts${a.replace(/count="\d+"/, `count="${fmts.length + 1}"`)}>${inner}${el}</numFmts>`)
        : xml.replace(/(<styleSheet\b[^>]*>)/, `$1<numFmts count="1">${el}</numFmts>`);
    }
  }
  const block = /<cellXfs\b([^>]*)>([\s\S]*?)<\/cellXfs>/.exec(xml);
  if (!block) return null;
  const xfs = block[2]!.match(/<xf\b[^>]*\/>|<xf\b[^>]*>[\s\S]*?<\/xf>/g) ?? [];
  const baseIdx = xfs[baseXf] ? baseXf : 0;
  const base = xfs[baseIdx];
  if (!base) return null;
  if (Number(/\bnumFmtId="(\d+)"/.exec(base)?.[1] ?? 0) === id) return { xml, index: baseIdx };
  const openEnd = base.search(/\/?>/);
  let open = base.slice(0, openEnd);
  open = /\bnumFmtId="/.test(open) ? open.replace(/\bnumFmtId="\d+"/, `numFmtId="${id}"`) : `${open} numFmtId="${id}"`;
  open = /\bapplyNumberFormat="/.test(open) ? open.replace(/\bapplyNumberFormat="[^"]*"/, 'applyNumberFormat="1"') : `${open} applyNumberFormat="1"`;
  const index = xfs.length;
  xml = xml.slice(0, block.index)
    + `<cellXfs${block[1]!.replace(/count="\d+"/, `count="${index + 1}"`)}>${block[2]}${open}${base.slice(openEnd)}</cellXfs>`
    + xml.slice(block.index + block[0].length);
  return { xml, index };
}

/** A cell's style index (its `s`); 0 when it has none or is not there. */
function cellStyleIndex(xml: string, ref: string): number {
  const m = new RegExp(`<c r="${ref}"([^>]*?)/?>`).exec(xml);
  return Number(/ s="(\d+)"/.exec(m?.[1] ?? '')?.[1] ?? 0);
}

/** Point a cell at style `s`. */
function setCellStyleIndex(xml: string, ref: string, s: number): string {
  return xml.replace(new RegExp(`<c r="${ref}"([^>]*?)(/?>)`), (_m, attrs: string, end: string) =>
    `<c r="${ref}"${/ s="\d+"/.test(attrs) ? attrs.replace(/ s="\d+"/, ` s="${s}"`) : `${attrs} s="${s}"`}${end}`);
}

/** Resolve each sheet NAME to its part path (`xl/worksheets/sheetN.xml`). */
async function sheetPaths(zip: JSZip): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const wbXml = await zip.file('xl/workbook.xml')?.async('string');
  const relXml = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');
  if (!wbXml || !relXml) return out;

  const targets = new Map<string, string>();
  for (const m of relXml.matchAll(/<Relationship([^>]*)\/>/g)) {
    const attrs = m[1] ?? '';
    const id = /Id="([^"]+)"/.exec(attrs)?.[1];
    const target = /Target="([^"]+)"/.exec(attrs)?.[1];
    if (id && target) targets.set(id, target.replace(/^\/?xl\//, '').replace(/^\//, ''));
  }
  for (const m of wbXml.matchAll(/<sheet\b([^>]*)\/?>/g)) {
    const attrs = m[1] ?? '';
    const name = /name="([^"]*)"/.exec(attrs)?.[1];
    const rid = /r:id="([^"]+)"/.exec(attrs)?.[1];
    if (!name || !rid) continue;
    const t = targets.get(rid);
    if (t) out.set(name, `xl/${t}`);
  }
  return out;
}

/**
 * Patch the given cells into the workbook, touching nothing else.
 *
 * @param originalBuf the workbook exactly as it came from the server
 * @param editsBySheet sheet NAME → the cells to write
 * @returns a new .xlsx buffer, or the original untouched if nothing applied
 */
export async function patchWorkbookCells(
  originalBuf: ArrayBuffer,
  editsBySheet: Record<string, SheetCellEdit[]>,
  recalcBySheet: Record<string, SheetCellEdit[]> = {},
  formulaBySheet: Record<string, SheetFormulaEdit[]> = {},
  /** Run on EVERY worksheet's XML after its cells are patched, inside the same unzip / zip — the Save's
   *  conditional-formatting expansion + repair used to unzip and re-zip the whole workbook twice more
   *  (~1 s on VOC, 2026-10-09). Returns the XML unchanged when it has nothing to do. */
  postSheetXml?: (xml: string) => string,
): Promise<ArrayBuffer> {
  return patchWorkbookCellsWith(originalBuf, editsBySheet, recalcBySheet, formulaBySheet, RowSegmentedSheet.open, postSheetXml);
}

/** The cell-at-a-time patcher over the WHOLE sheet XML — what patchWorkbookCells did before 2026-09-30.
 *  Kept as the reference the row-by-row patcher is checked against (sdmsXlsxPatch.test.ts, the save-patch
 *  harness): both must produce the same bytes. */
export async function patchSheetXmlReference(
  originalBuf: ArrayBuffer,
  editsBySheet: Record<string, SheetCellEdit[]>,
  recalcBySheet: Record<string, SheetCellEdit[]> = {},
  formulaBySheet: Record<string, SheetFormulaEdit[]> = {},
): Promise<ArrayBuffer> {
  return patchWorkbookCellsWith(originalBuf, editsBySheet, recalcBySheet, formulaBySheet, xml => new WholeSheet(xml));
}

/** The per-cell operations a sheet part needs. `WholeSheet` runs them on the whole XML string;
 *  `RowSegmentedSheet` runs the SAME functions on just the target cell's `<row>` (speed, 2026-09-30). */
interface SheetXml {
  patchCell(ref: string, value: unknown, text: boolean): void;
  patchFormulaCache(ref: string, value: unknown): void;
  patchCellFormula(ref: string, formula: string, value: unknown): void;
  cellStyleIndex(ref: string): number;
  setCellStyleIndex(ref: string, s: number): void;
  toString(): string;
}

class WholeSheet implements SheetXml {
  constructor(private xml: string) {}
  patchCell(ref: string, value: unknown, text: boolean) { this.xml = patchCell(this.xml, ref, value, text); }
  patchFormulaCache(ref: string, value: unknown) { this.xml = patchFormulaCache(this.xml, ref, value); }
  patchCellFormula(ref: string, formula: string, value: unknown) { this.xml = patchCellFormula(this.xml, ref, formula, value); }
  cellStyleIndex(ref: string) { return cellStyleIndex(this.xml, ref); }
  setCellStyleIndex(ref: string, s: number) { this.xml = setCellStyleIndex(this.xml, ref, s); }
  toString() { return this.xml; }
}

/**
 * The sheet XML cut into its `<row>` elements once, so each edit runs the unchanged cell functions on ONE
 * row instead of the whole part. A save refreshes the cached value of every formula cell whose result
 * changed — thousands after a merge or an import — and each whole-part regex scan + rebuild made that
 * rows × edits (seconds on a VOC / Metals sheet). A cell only ever sits in its own row, so every function
 * finds and rewrites exactly what it did on the whole string; the few cases that are not row-local fall back
 * to the whole string: a row that must be inserted is placed exactly where the whole-string insert puts it,
 * and a formula-cache refresh of a SELF-CLOSING cell (whose lazy match can run past the row) runs on the
 * whole XML. A part that does not split cleanly (no `</sheetData>`, text between rows) is patched whole.
 */
class RowSegmentedSheet implements SheetXml {
  private rowNum: number[] = [];
  private index = new Map<number, number>();
  private constructor(private head: string, private rows: string[], private tail: string) { this.reindex(); }

  static open(xml: string): SheetXml {
    const start = xml.indexOf('<sheetData>');
    const end = xml.lastIndexOf('</sheetData>');
    if (start < 0 || end < start) return new WholeSheet(xml);
    const rows: string[] = [];
    const re = /<row\b[^>]*?(?:\/>|>[\s\S]*?<\/row>)/y;
    let pos = start + '<sheetData>'.length;
    while (pos < end) {
      re.lastIndex = pos;
      const m = re.exec(xml);
      if (!m || m.index !== pos || pos + m[0].length > end) return new WholeSheet(xml);
      rows.push(m[0]);
      pos += m[0].length;
    }
    return new RowSegmentedSheet(xml.slice(0, start + '<sheetData>'.length), rows, xml.slice(end));
  }

  private reindex() {
    this.index.clear();
    this.rowNum = this.rows.map(r => { const m = /^<row r="(\d+)"/.exec(r); return m ? parseInt(m[1]!, 10) : NaN; });
    this.rowNum.forEach((n, i) => { if (Number.isFinite(n) && !this.index.has(n)) this.index.set(n, i); });
  }
  private rowOf(ref: string): number | undefined { return this.index.get(parseInt(ref.replace(/^[A-Z]+/, ''), 10)); }
  /** Set once a whole-string operation left XML that no longer cuts cleanly: every later edit goes there. */
  private wholeDoc: WholeSheet | null = null;
  /** Run a whole-string operation, then cut the result again (rare paths only). */
  private whole(op: (xml: string) => string) {
    const next = RowSegmentedSheet.open(op(this.toString()));
    if (next instanceof RowSegmentedSheet) { this.head = next.head; this.rows = next.rows; this.tail = next.tail; this.reindex(); }
    else this.wholeDoc = next as WholeSheet;
  }

  patchCell(ref: string, value: unknown, text: boolean) {
    if (this.wholeDoc) { this.wholeDoc.patchCell(ref, value, text); return; }
    const i = this.rowOf(ref);
    if (i !== undefined) { this.rows[i] = patchCell(this.rows[i]!, ref, value, text); return; }
    // No such row: patchCell's own case 3 — a new <row> before the first later row, else at the end.
    const n = parseInt(ref.replace(/^[A-Z]+/, ''), 10);
    const newRow = `<row r="${n}">${buildCell(ref, '', value, text)}</row>`;
    const at = this.rowNum.findIndex(r => r > n);
    if (at >= 0) this.rows.splice(at, 0, newRow); else this.rows.push(newRow);
    this.reindex();
  }
  patchFormulaCache(ref: string, value: unknown) {
    if (this.wholeDoc) { this.wholeDoc.patchFormulaCache(ref, value); return; }
    const i = this.rowOf(ref);
    if (i === undefined) return;                                   // no row → no cell: unchanged, as on the whole string
    if (new RegExp(`<c r="${ref}"[^>]*/>`).test(this.rows[i]!)) { this.whole(x => patchFormulaCache(x, ref, value)); return; }
    this.rows[i] = patchFormulaCache(this.rows[i]!, ref, value);
  }
  patchCellFormula(ref: string, formula: string, value: unknown) {
    if (this.wholeDoc) { this.wholeDoc.patchCellFormula(ref, formula, value); return; }
    const i = this.rowOf(ref);
    if (i !== undefined) this.rows[i] = patchCellFormula(this.rows[i]!, ref, formula, value);
  }
  cellStyleIndex(ref: string) {
    if (this.wholeDoc) return this.wholeDoc.cellStyleIndex(ref);
    const i = this.rowOf(ref);
    return i === undefined ? 0 : cellStyleIndex(this.rows[i]!, ref);
  }
  setCellStyleIndex(ref: string, s: number) {
    if (this.wholeDoc) { this.wholeDoc.setCellStyleIndex(ref, s); return; }
    const i = this.rowOf(ref);
    if (i !== undefined) this.rows[i] = setCellStyleIndex(this.rows[i]!, ref, s);
  }
  toString() { return this.wholeDoc ? this.wholeDoc.toString() : this.head + this.rows.join('') + this.tail; }
}

async function patchWorkbookCellsWith(
  originalBuf: ArrayBuffer,
  editsBySheet: Record<string, SheetCellEdit[]>,
  recalcBySheet: Record<string, SheetCellEdit[]>,
  formulaBySheet: Record<string, SheetFormulaEdit[]>,
  openSheet: (xml: string) => SheetXml,
  postSheetXml?: (xml: string) => string,
): Promise<ArrayBuffer> {
  const zip = await JSZip.loadAsync(originalBuf);
  const paths = await sheetPaths(zip);
  let touched = 0;
  let styles: string | null = null;                   // xl/styles.xml, loaded on the first number-format edit
  const styleFor = new Map<string, number>();         // "baseStyle|format" → the style index to use
  // A workbook already carrying an `[object Object]` number format is repaired on every save, so a batch saved
  // that way opens in ALPACA again once it is saved from here (see repairObjectNumFmts).
  const loadedStyles = (await zip.file('xl/styles.xml')?.async('string')) ?? null;
  const stylesRepaired = loadedStyles !== null && repairObjectNumFmts(loadedStyles) !== loadedStyles;
  if (stylesRepaired) styles = repairObjectNumFmts(loadedStyles!);

  const sheets = new Set([
    ...Object.keys(editsBySheet), ...Object.keys(recalcBySheet), ...Object.keys(formulaBySheet),
  ]);
  for (const sheetName of sheets) {
    const edits = editsBySheet[sheetName] ?? [];
    const recalc = recalcBySheet[sheetName] ?? [];
    const formulas = formulaBySheet[sheetName] ?? [];
    if (!edits.length && !recalc.length && !formulas.length) continue;
    const path = paths.get(sheetName);
    if (!path) continue;
    const file = zip.file(path);
    if (!file) continue;
    const sheet = openSheet(await file.async('string'));
    for (const e of edits) {
      sheet.patchCell(a1(e.row, e.col), e.value, e.text === true);
      touched++;
    }
    // Dependent formulas keep their `<f>`; only the cached `<v>` is refreshed.
    for (const r of recalc) {
      sheet.patchFormulaCache(a1(r.row, r.col), r.value);
      touched++;
    }
    // Apply-Rounding wraps: (re)write the cell's `<f>` to the `_LIMSRound(...)` wrap
    // so the saved blob carries the rounding marker (OG RoundCellInPlace parity).
    for (const f of formulas) {
      const ref = a1(f.row, f.col);
      sheet.patchCellFormula(ref, f.formula, f.value);
      if (f.numFmt) {
        styles ??= (await zip.file('xl/styles.xml')?.async('string')) ?? '';
        const base = sheet.cellStyleIndex(ref);
        let idx = styleFor.get(`${base}|${f.numFmt}`);
        if (idx === undefined && styles) {
          const added = addNumFmtStyle(styles, base, f.numFmt);
          if (added) { styles = added.xml; idx = added.index; styleFor.set(`${base}|${f.numFmt}`, idx); }
        }
        if (idx !== undefined && idx !== base) sheet.setCellStyleIndex(ref, idx);
      }
      touched++;
    }
    zip.file(path, sheet.toString(), { createFolders: false });
  }

  // The post step runs on every worksheet, patched or not, exactly as the separate passes did.
  let postChanged = false;
  if (postSheetXml) {
    for (const path of Object.keys(zip.files)) {
      if (!/^xl\/worksheets\/sheet\d+\.xml$/.test(path)) continue;
      const xml = await zip.file(path)!.async('string');
      const out = postSheetXml(xml);
      if (out !== xml) { zip.file(path, out, { createFolders: false }); postChanged = true; }
    }
  }

  if (touched === 0 && !postChanged && !stylesRepaired) return originalBuf;
  if (styles) zip.file('xl/styles.xml', styles, { createFolders: false });
  if (touched === 0) return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' }) as Promise<ArrayBuffer>;

  // DROP THE STALE CALC CHAIN — the last piece of the blank-sheet bug, and the
  // one that actually decides it. xl/calcChain.xml caches the evaluation order of
  // every FORMULA cell. Writing a literal over a formula (which is what an edit
  // does) leaves the chain pointing at a cell that no longer has one, and
  // DevExpress rejects the WHOLE workbook for it — measured on a surgically
  // patched file that was otherwise byte-identical to the original:
  //
  //     calcChain kept    -> LoadDocument=false, 1 empty sheet
  //     calcChain dropped -> LoadDocument=true,  3 sheets, edit present
  //
  // The chain is a rebuildable cache: Excel and DevExpress regenerate it on the
  // next calculation, so removing it is what Excel itself does. Its relationship
  // and content-type override go too, or the package references a missing part.
  const chain = 'xl/calcChain.xml';
  if (zip.file(chain)) {
    zip.remove(chain);
    const rels = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');
    if (rels)
      zip.file('xl/_rels/workbook.xml.rels',
        rels.replace(/<Relationship[^>]*calcChain\.xml"[^>]*\/>/g, ''), { createFolders: false });
    const ct = await zip.file('[Content_Types].xml')?.async('string');
    if (ct)
      zip.file('[Content_Types].xml',
        ct.replace(/<Override[^>]*calcChain\.xml"[^>]*\/>/g, ''), { createFolders: false });
  }

  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' }) as Promise<ArrayBuffer>;
}

/** The summary sheets OG always protects (TransformDataTable / TransformDataTablebySample lock every cell and
 *  Protect, SpreadsheetEntryController :29749-29759 / :30031-30041). */
export const OG_PROTECTED_SUMMARY_SHEETS: readonly string[] = ['Parameter-Oriented Summary', 'Sample-Oriented Summary'];

/** OG's protection element, byte-for-byte as its saved workbooks carry it: password "password" (legacy hash
 *  83AF), autofilters allowed. */
const OG_SHEET_PROTECTION = '<sheetProtection sheet="1" autoFilter="0" objects="1" scenarios="1" selectUnlockedCells="1" password="83AF"/>';

/** Put OG's `<sheetProtection>` into one worksheet part, where the schema wants it (right after sheetData /
 *  sheetCalcPr); an existing one is replaced. Unchanged when the part has no sheetData. */
export function withOgSheetProtection(xml: string): string {
  const existing = /<sheetProtection\b[^>]*\/>/.exec(xml);
  if (existing) return xml.slice(0, existing.index) + OG_SHEET_PROTECTION + xml.slice(existing.index + existing[0].length);
  const anchor = /<sheetCalcPr\b[^>]*\/>/.exec(xml) ?? /<\/sheetData>/.exec(xml) ?? /<sheetData\s*\/>/.exec(xml);
  if (!anchor) return xml;
  const at = anchor.index + anchor[0].length;
  return xml.slice(0, at) + OG_SHEET_PROTECTION + xml.slice(at);
}

/**
 * Protect `sheetNames` in the SAVED workbook as OG's save does — the two summaries, Raw Data, and Reagents /
 * Instruments when OG locks them. AI protected none, so OG opened an AI batch with those sheets editable (OG vs AI
 * comparison 2026-09-28, point 12). Persist-only and surgical like patchWorkbookCells: only the named sheets'
 * parts change. Returns the buffer untouched when none of them exists.
 */
export async function protectSheetsLikeOg(buf: ArrayBuffer, sheetNames: readonly string[]): Promise<ArrayBuffer> {
  if (!sheetNames.length) return buf;
  const zip = await JSZip.loadAsync(buf);
  const paths = await sheetPaths(zip);
  let changed = false;
  for (const name of new Set(sheetNames)) {
    const path = paths.get(name);
    const xml = path ? await zip.file(path)?.async('string') : undefined;
    if (!path || !xml) continue;
    const next = withOgSheetProtection(xml);
    if (next !== xml) { zip.file(path, next); changed = true; }
  }
  return changed ? zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' }) as Promise<ArrayBuffer> : buf;
}
